import { RouteLocationNormalized } from 'vue-router'

/**
 * Slim FE registry for route guards + axios pre-checks.
 * Full Add User catalog: GET /api/permissions/catalog (api permissionCatalog.js).
 * Regenerate: node api/src/tools/syncPermissionCatalogToFront.js
 */

type RoutePage = { key: string; routeNames: string[]; paths: string[] }

type ClientMatcher = {
  method: string
  pattern: string
  key?: string | null
  anyOf?: string[]
  when?: (body: any, query?: any) => boolean
}

const routePages: RoutePage[] = [
  {
    "key": "page.dashboard",
    "routeNames": [
      "dashboard"
    ],
    "paths": [
      "/"
    ]
  },
  {
    "key": "page.customersLink",
    "routeNames": [
      "CustomersLink"
    ],
    "paths": [
      "/customers-link"
    ]
  },
  {
    "key": "page.customerDetails",
    "routeNames": [
      "customerDetails"
    ],
    "paths": [
      "/installations-list/:id"
    ]
  },
  {
    "key": "page.customerDrawer",
    "routeNames": [],
    "paths": []
  },
  {
    "key": "page.installationsList",
    "routeNames": [
      "installationsList"
    ],
    "paths": [
      "/installations-list"
    ]
  },
  {
    "key": "page.customerForm",
    "routeNames": [
      "customerForm",
      "convertOfflineCustomer"
    ],
    "paths": [
      "/installations-list/customerForm/:id?",
      "/installations-list/customerForm/convert/:id"
    ]
  },
  {
    "key": "page.offlineCustomers",
    "routeNames": [
      "OfflineCustomers"
    ],
    "paths": [
      "/offline-customers"
    ]
  },
  {
    "key": "page.resellers",
    "routeNames": [
      "resellers"
    ],
    "paths": [
      "/resellers"
    ]
  },
  {
    "key": "page.equipment",
    "routeNames": [
      "equipmentList"
    ],
    "paths": [
      "/equipment/list"
    ]
  },
  {
    "key": "page.installations",
    "routeNames": [
      "installations",
      "installationsAssign",
      "taskAssignment"
    ],
    "paths": [
      "/installations",
      "/installations/assign",
      "/tasks/assign"
    ]
  },
  {
    "key": "page.tasks",
    "routeNames": [
      "installer_tasks"
    ],
    "paths": [
      "/tasks"
    ]
  },
  {
    "key": "page.cashSettings",
    "routeNames": [
      "CashSettings"
    ],
    "paths": [
      "/cash-settings",
      "/quickbooks-accounts",
      "/quickbooks-payees",
      "/payees",
      "/cash-books",
      "/cash-boxes"
    ]
  },
  {
    "key": "page.billing",
    "routeNames": [
      "billing"
    ],
    "paths": [
      "/billing"
    ]
  },
  {
    "key": "page.serviceDiscounts",
    "routeNames": [
      "serviceDiscounts"
    ],
    "paths": [
      "/service-discounts"
    ]
  },
  {
    "key": "page.receivePayment",
    "routeNames": [
      "payment",
      "receivePayment"
    ],
    "paths": [
      "/payments/payment/:customerId?",
      "/payments/receive/:customerId"
    ]
  },
  {
    "key": "page.cashLedger",
    "routeNames": [
      "cashLedger"
    ],
    "paths": [
      "/payments/cash-ledger",
      "/cash-books",
      "/cash-boxes"
    ]
  },
  {
    "key": "page.paymentQueue",
    "routeNames": [
      "paymentQueue"
    ],
    "paths": [
      "/payments/queue"
    ]
  },
  {
    "key": "page.paymentsList",
    "routeNames": [
      "payments"
    ],
    "paths": [
      "/payments"
    ]
  },
  {
    "key": "page.hotspotPayments",
    "routeNames": [
      "hotspotPayment"
    ],
    "paths": [
      "/payments/hotspot/:customerId?"
    ]
  },
  {
    "key": "page.qboSyncReview",
    "routeNames": [
      "QuickBooksSyncReview",
      "QuickBooksSyncReviewV2"
    ],
    "paths": [
      "/quickbooks-sync-review",
      "/quickbooks-sync-review-v2"
    ]
  },
  {
    "key": "page.hotspotParents",
    "routeNames": [
      "hotspotCustomers",
      "hotspotParents",
      "hotspotCustomerDetails"
    ],
    "paths": [
      "/hotspot-customers",
      "/hotspot-parents"
    ]
  },
  {
    "key": "page.hotspotCreatePackage",
    "routeNames": [],
    "paths": []
  },
  {
    "key": "page.hotspotAllowances",
    "routeNames": [
      "hotspotAllowances"
    ],
    "paths": [
      "/hotspot-allowances"
    ]
  },
  {
    "key": "page.branches",
    "routeNames": [
      "branches"
    ],
    "paths": [
      "/branches"
    ]
  },
  {
    "key": "page.users",
    "routeNames": [
      "users",
      "Users"
    ],
    "paths": [
      "/users"
    ]
  },
  {
    "key": "page.settings",
    "routeNames": [
      "settings",
      "QuickBooksAdmin",
      "QuickBooksConnect"
    ],
    "paths": [
      "/settings",
      "/quickbooks-admin",
      "/quickbooks-connect"
    ]
  }
]

const matchers: ClientMatcher[] = [
  { method: "GET", pattern: "^/api/noc/dashboard/summary$", key: "page.dashboard" },
  { method: "GET", pattern: "^/api/qbo-customer-links$", key: "page.customersLink", when: (_body, query) => !query?.customerType },
  { method: "GET", pattern: "^/api/qbo-customer-links$", key: "action.customersLink.tabPpoe", when: (_body, query) => String(query?.customerType || "") === "ppoe" },
  { method: "POST", pattern: "^/api/qbo-customer-links/create-qb-customer$", anyOf: ["action.customer.completeQbRegistration","action.installationsList.completeQbRegistration"] },
  { method: "GET", pattern: "^/api/installations/(?!assigned-tasks$)[^/]+$", anyOf: ["action.customer.viewInstallation","action.installationsList.viewInstallation","action.installations.editForm","action.noc.approveChecklist","page.tasks"] },
  { method: "GET", pattern: "^/api/qbo-customer-links/[^/]+/customer-details$", anyOf: ["action.customer.viewInstallation","action.installationsList.viewInstallation","action.installation.createPpoe","action.customer.edit","action.installationsList.edit","page.customersLink","action.customersLink.tabPpoe"] },
  { method: "GET", pattern: "^/api/customers/[^/]+/profile$", anyOf: ["page.customerDetails","action.customer.viewDetails","action.installationsList.viewDetails","page.installationsList"] },
  { method: "GET", pattern: "^/api/customers/[^/]+/qbo$", anyOf: ["action.customerDetails.tabQbo","page.customerDetails","action.customer.viewDetails","action.installationsList.viewDetails","action.installationsList.viewQboTab","action.customer.viewQboTab"] },
  { method: "GET", pattern: "^/api/customers/[^/]+/sib$", anyOf: ["action.customerDetails.tabSib","action.customerDetails.tabNoc","page.customerDetails","action.customer.viewDetails","action.installationsList.viewDetails"] },
  { method: "GET", pattern: "^/api/customers/[^/]+/noc$", anyOf: ["action.customerDetails.tabInstallation"] },
  { method: "POST", pattern: "^/api/users/change-visp$", anyOf: ["action.sib.changeVisp","action.payment.manageSib"] },
  { method: "POST", pattern: "^/api/users/change-status$", anyOf: ["action.sib.changeStatus","action.payment.manageSib"] },
  { method: "POST", pattern: "^/api/users/deltasib/schedule-status$", anyOf: ["action.sib.scheduleStop","action.payment.manageSib"] },
  { method: "DELETE", pattern: "^/api/users/deltasib/schedule-status/[^/]+$", anyOf: ["action.sib.cancelScheduledStop","action.payment.manageSib"] },
  { method: "POST", pattern: "^/api/invoices/services/add-pending$", anyOf: ["action.payment.addPendingService","action.payment.manageServices"] },
  { method: "POST", pattern: "^/api/invoices/services/change-package$", anyOf: ["action.payment.rechargeService","action.payment.manageServices"] },
  { method: "POST", pattern: "^/api/invoices/services/start-next-pending$", anyOf: ["action.service.startNextPending","action.payment.manageServices"], when: (body) => body?.preview !== true },
  { method: "PUT", pattern: "^/api/customers/[^/]+$", anyOf: ["action.customer.edit","action.installationsList.edit"] },
  { method: "PUT", pattern: "^/api/qbo-customer-links/[^/]+$", anyOf: ["action.customer.edit","action.installationsList.edit"] },
  { method: "PUT", pattern: "^/api/customers/[^/]+$", anyOf: ["action.customer.syncLive","action.installationsList.syncLive"], when: (body) => body?.syncLive === true },
  { method: "GET", pattern: "^/api/qbo-customer-links/[^/]+/compare-remote$", anyOf: ["action.customer.retrieveRemote","action.installationsList.retrieveRemote"] },
  { method: "POST", pattern: "^/api/qbo-customer-links/[^/]+/apply-remote-sync$", anyOf: ["action.customer.applyRemoteSync","action.installationsList.applyRemoteSync"] },
  { method: "PATCH", pattern: "^/api/customers/[^/]+/paperwork-status$", anyOf: ["action.customer.updatePaperwork","action.installationsList.updatePaperwork"] },
  { method: "GET", pattern: "^/api/qbo-customer-links$", key: "page.installationsList", when: (_body, query) => String(query?.customerType || "") === "crm" },
  { method: "GET", pattern: "^/api/customers/[^/]+$", key: "page.customerForm" },
  { method: "GET", pattern: "^/api/branches", anyOf: ["page.branches","page.settings.radiusServers","page.users","page.installations","page.customerForm","page.offlineCustomers","page.resellers","page.serviceDiscounts","page.billing","page.paymentsList","page.paymentQueue"] },
  { method: "POST", pattern: "^/api/customers$", key: "action.customer.create" },
  { method: "POST", pattern: "^/api/qbo-sync-unsynced/convert-offline-customer$", key: "action.customer.convertOffline" },
  { method: "GET", pattern: "^/api/qbo-sync-unsynced/returned-offline-users$", key: "page.offlineCustomers" },
  { method: "GET", pattern: "^/api/radius-servers$", anyOf: ["page.billing","action.billing.viewAllServers","page.serviceDiscounts","action.serviceDiscount.viewAllServers","page.qboSyncReview","action.qboSync.viewAllServers","page.receivePayment","action.payment.viewAllServers","page.hotspotPayments","action.hotspotPayment.viewAllServers","page.paymentsList","action.paymentsList.viewAllServers","page.paymentQueue","action.paymentQueue.viewAllServers","page.offlineCustomers","action.offlineCustomer.viewAllServers","page.settings.radiusServers","page.installations","page.hotspotParents","page.hotspotCustomers","page.hotspotCreatePackage","page.hotspotAllowances","action.hotspotParent.create","action.hotspotParent.edit","action.hotspotCustomer.create","action.hotspotCustomer.edit","action.hotspotPackage.create"] },
  { method: "POST", pattern: "^/api/qbo-sync-unsynced/refresh-last-request-dt$", key: "action.offlineCustomer.syncSibDates" },
  { method: "PATCH", pattern: "^/api/qbo-sync-unsynced/items/[^/]+$", anyOf: ["action.offlineCustomer.link","action.qboSync.resolveConflicts"] },
  { method: "POST", pattern: "^/api/resellers$", key: "action.reseller.create" },
  { method: "PUT", pattern: "^/api/resellers/[^/]+$", key: "action.reseller.edit" },
  { method: "DELETE", pattern: "^/api/resellers/[^/]+$", key: "action.reseller.delete" },
  { method: "POST", pattern: "^/api/equipment$", key: "action.equipment.create" },
  { method: "PUT", pattern: "^/api/equipment/[^/]+$", key: "action.equipment.edit" },
  { method: "DELETE", pattern: "^/api/equipment/[^/]+$", key: "action.equipment.delete" },
  { method: "POST", pattern: "^/api/services/sync/pull$", key: "action.service.import" },
  { method: "PATCH", pattern: "^/api/services/sync/items/[^/]+/price$", anyOf: ["action.service.map", "action.equipment.edit"] },
  { method: "PATCH", pattern: "^/api/services/sync/items/[^/]+$", key: "action.service.map" },
  { method: "GET", pattern: "^/api/installations$", key: "page.installations" },
  { method: "GET", pattern: "^/api/servary-tickets$", anyOf: ["action.installations.tabFeedback","page.servaryTickets"] },
  { method: "PATCH", pattern: "^/api/servary-tickets/[^/]+$", key: "action.installations.feedback.assign", when: (body) => body && Object.prototype.hasOwnProperty.call(body, "assignedTo") },
  { method: "PATCH", pattern: "^/api/servary-tickets/[^/]+$", key: "action.servaryTicket.update" },
  { method: "POST", pattern: "^/api/servary-tickets/[^/]+/comments$", key: "action.servaryTicket.comment" },
  { method: "POST", pattern: "^/api/installations/assign$", key: "action.task.assign" },
  { method: "GET", pattern: "^/api/installations/assigned-tasks$", anyOf: ["page.tasks","action.task.assign"] },
  { method: "DELETE", pattern: "^/api/installations/unassign/[^/]+$", key: "action.task.unassign" },
  { method: "GET", pattern: "^/api/qbo-customer-links/[^/]+/customer-details$", anyOf: ["action.customer.viewInstallation","action.installationsList.viewInstallation","action.installation.createPpoe","action.customer.edit","action.installationsList.edit","page.customersLink","action.customersLink.tabPpoe"] },
  { method: "POST", pattern: "^/api/qbo-customer-links/register-ppoe$", key: "action.installation.createPpoe" },
  { method: "PUT", pattern: "^/api/installations/[^/]+$", anyOf: ["action.installations.editForm","action.installations.updateStatus","action.installer.completeTask","page.tasks"] },
  { method: "PUT", pattern: "^/api/installations/[^/]+/approve$", anyOf: ["action.installations.editForm","action.noc.approveChecklist"] },
  { method: "GET", pattern: "^/api/installations/(?!assigned-tasks$)[^/]+$", anyOf: ["action.installations.editForm","action.noc.approveChecklist","page.tasks","action.customer.viewInstallation","action.installationsList.viewInstallation"] },
  { method: "DELETE", pattern: "^/api/installations/[^/]+$", key: "action.installation.delete" },
  { method: "GET", pattern: "^/api/installations/assigned-tasks$", anyOf: ["page.tasks","action.task.assign"] },
  { method: "GET", pattern: "^/api/installations/(?!assigned-tasks$)[^/]+$", anyOf: ["page.tasks","action.installations.editForm","action.noc.approveChecklist","action.customer.viewInstallation","action.installationsList.viewInstallation"] },
  { method: "PUT", pattern: "^/api/installations/[^/]+$", anyOf: ["page.tasks","action.installations.editForm","action.installations.updateStatus","action.installer.completeTask"] },
  { method: "POST", pattern: "^/api/installations/[^/]+/photos$", anyOf: ["page.tasks","action.installer.uploadPhotos"] },
  { method: "DELETE", pattern: "^/api/installations/[^/]+/photos/[^/]+$", anyOf: ["page.tasks","action.installer.deletePhoto"] },
  { method: "POST", pattern: "^/api/installations/[^/]+/photos$", anyOf: ["action.installer.uploadPhotos","page.tasks"] },
  { method: "DELETE", pattern: "^/api/installations/[^/]+/photos/[^/]+$", anyOf: ["action.installer.deletePhoto","page.tasks"] },
  { method: "GET", pattern: "^/api/finance/cash/books$", anyOf: ["page.cashLedger","page.cashSettings"] },
  { method: "GET", pattern: "^/api/finance/cash/boxes$", anyOf: ["page.cashLedger","page.cashSettings"] },
  { method: "PUT", pattern: "^/api/finance/cash/books/[^/]+$", anyOf: ["action.cashSettings.editBook","page.cashSettings"] },
  { method: "PUT", pattern: "^/api/finance/cash/boxes/[^/]+$", anyOf: ["action.cashSettings.editBook","page.cashSettings"] },
  { method: "PUT", pattern: "^/api/finance/cash/books/[^/]+/map-qbo$", anyOf: ["action.cashSettings.editBook","page.cashSettings"] },
  { method: "PUT", pattern: "^/api/finance/cash/boxes/[^/]+/map-qbo$", anyOf: ["action.cashSettings.editBook","page.cashSettings"] },
  { method: "GET", pattern: "^/api/billing$", key: "page.billing" },
  { method: "GET", pattern: "^/api/billing/centers$", key: "page.billing" },
  { method: "GET", pattern: "^/api/billing/visps$", key: "page.billing" },
  { method: "GET", pattern: "^/api/billing/edited-count$", key: "page.billing" },
  { method: "GET", pattern: "^/api/billing/[^/]+/history$", key: "page.billing" },
  { method: "GET", pattern: "^/api/billing/[^/]+/qb-context$", key: "page.billing" },
  { method: "GET", pattern: "^/api/billing/[^/]+/invoice$", key: "page.billing" },
  { method: "GET", pattern: "^/api/radius-servers$", anyOf: ["page.billing","action.billing.viewAllServers","page.serviceDiscounts","action.serviceDiscount.viewAllServers","page.qboSyncReview","action.qboSync.viewAllServers","page.receivePayment","action.payment.viewAllServers","page.hotspotPayments","action.hotspotPayment.viewAllServers","page.paymentsList","action.paymentsList.viewAllServers","page.paymentQueue","action.paymentQueue.viewAllServers","page.offlineCustomers","action.offlineCustomer.viewAllServers","page.settings.radiusServers","page.installations","page.hotspotParents","page.hotspotCustomers","page.hotspotCreatePackage","page.hotspotAllowances","action.hotspotParent.create","action.hotspotParent.edit","action.hotspotCustomer.create","action.hotspotCustomer.edit","action.hotspotPackage.create"] },
  { method: "POST", pattern: "^/api/billing/sync$", key: "action.billing.sync" },
  { method: "POST", pattern: "^/api/billing/load-new$", key: "action.billing.loadNew" },
  { method: "POST", pattern: "^/api/billing/bulk-classify$", key: "action.billing.bulkClassify" },
  { method: "POST", pattern: "^/api/billing/retry-all$", key: "action.billing.retryQbLink" },
  { method: "POST", pattern: "^/api/billing/retry/[^/]+$", key: "action.billing.retryQbLink" },
  { method: "POST", pattern: "^/api/billing/recheck/[^/]+$", key: "action.billing.recheck" },
  { method: "PATCH", pattern: "^/api/billing/[^/]+$", key: "action.billing.editDiscount", when: (body) => body && typeof body === 'object' && 'discount' in body },
  { method: "PATCH", pattern: "^/api/billing/[^/]+$", key: "action.billing.editAmount", when: (body) => body && typeof body === 'object' && 'amount' in body },
  { method: "PATCH", pattern: "^/api/billing/[^/]+$", key: "action.billing.editDevicePrices", when: (body) => body && typeof body === 'object' && 'devices' in body },
  { method: "PUT", pattern: "^/api/billing/[^/]+/manual$", key: "action.billing.setManual" },
  { method: "PUT", pattern: "^/api/billing/[^/]+/hide$", key: "action.billing.hide" },
  { method: "PUT", pattern: "^/api/billing/[^/]+/memo$", key: "action.billing.editMemo" },
  { method: "GET", pattern: "^/api/billing/invoice-credits/[^/]+$", key: "action.billing.viewCredits" },
  { method: "POST", pattern: "^/api/billing/refresh-credit/[^/]+$", key: "action.billing.viewCredits" },
  { method: "POST", pattern: "^/api/billing/apply-credits/[^/]+$", key: "action.billing.applyCredits" },
  { method: "POST", pattern: "^/api/billing/refresh-credit/[^/]+$", key: "action.billing.applyCredits" },
  { method: "GET", pattern: "^/api/billing/export$", key: "action.billing.export" },
  { method: "GET", pattern: "^/api/service-discounts$", key: "page.serviceDiscounts" },
  { method: "GET", pattern: "^/api/radius-servers$", anyOf: ["page.billing","action.billing.viewAllServers","page.serviceDiscounts","action.serviceDiscount.viewAllServers","page.qboSyncReview","action.qboSync.viewAllServers","page.receivePayment","action.payment.viewAllServers","page.hotspotPayments","action.hotspotPayment.viewAllServers","page.paymentsList","action.paymentsList.viewAllServers","page.paymentQueue","action.paymentQueue.viewAllServers","page.offlineCustomers","action.offlineCustomer.viewAllServers","page.settings.radiusServers","page.installations","page.hotspotParents","page.hotspotCustomers","page.hotspotCreatePackage","page.hotspotAllowances","action.hotspotParent.create","action.hotspotParent.edit","action.hotspotCustomer.create","action.hotspotCustomer.edit","action.hotspotPackage.create"] },
  { method: "POST", pattern: "^/api/service-discounts/sync$", key: "action.serviceDiscount.sync" },
  { method: "PUT", pattern: "^/api/service-discounts/[^/]+/status$", key: "action.customer.approveDiscount" },
  { method: "GET", pattern: "^/api/invoices/customer-invoices/[^/]+$", key: "page.receivePayment" },
  { method: "GET", pattern: "^/api/invoices/search-customers$", anyOf: ["page.receivePayment","page.paymentsList"] },
  { method: "GET", pattern: "^/api/invoices/service-docnumber-check$", anyOf: ["page.receivePayment","action.invoice.viewDetails"] },
  { method: "POST", pattern: "^/api/invoices/payment/queue$", anyOf: ["page.receivePayment","action.payment.receive"] },
  { method: "GET", pattern: "^/api/invoices/payment/[^/]+$", anyOf: ["page.receivePayment","action.payment.printReceipt"] },
  { method: "GET", pattern: "^/api/radius-servers$", anyOf: ["page.billing","action.billing.viewAllServers","page.serviceDiscounts","action.serviceDiscount.viewAllServers","page.qboSyncReview","action.qboSync.viewAllServers","page.receivePayment","action.payment.viewAllServers","page.hotspotPayments","action.hotspotPayment.viewAllServers","page.paymentsList","action.paymentsList.viewAllServers","page.paymentQueue","action.paymentQueue.viewAllServers","page.offlineCustomers","action.offlineCustomer.viewAllServers","page.settings.radiusServers","page.installations","page.hotspotParents","page.hotspotCustomers","page.hotspotCreatePackage","page.hotspotAllowances","action.hotspotParent.create","action.hotspotParent.edit","action.hotspotCustomer.create","action.hotspotCustomer.edit","action.hotspotPackage.create"] },
  { method: "POST", pattern: "^/api/users/change-visp$", anyOf: ["action.sib.changeVisp","action.payment.manageSib"] },
  { method: "POST", pattern: "^/api/users/change-status$", anyOf: ["action.sib.changeStatus","action.payment.manageSib"] },
  { method: "POST", pattern: "^/api/users/deltasib/schedule-status$", anyOf: ["action.sib.scheduleStop","action.payment.manageSib"] },
  { method: "DELETE", pattern: "^/api/users/deltasib/schedule-status/[^/]+$", anyOf: ["action.sib.cancelScheduledStop","action.payment.manageSib"] },
  { method: "POST", pattern: "^/api/invoices/services/add-pending$", anyOf: ["action.payment.addPendingService","action.payment.manageServices"] },
  { method: "POST", pattern: "^/api/invoices/services/change-package$", anyOf: ["action.payment.rechargeService","action.payment.manageServices"] },
  { method: "POST", pattern: "^/api/invoices/services/start-next-pending$", anyOf: ["action.service.startNextPending","action.payment.manageServices"], when: (body) => body?.preview !== true },
  { method: "POST", pattern: "^/api/invoices/services/add$", anyOf: ["action.payment.packageSelect","action.payment.manageServices"] },
  { method: "POST", pattern: "^/api/invoices/services/cancel$", anyOf: ["action.service.cancel","action.payment.manageServices"] },
  { method: "GET", pattern: "^/api/finance/cash/books$", anyOf: ["page.cashLedger","page.cashSettings"] },
  { method: "GET", pattern: "^/api/finance/cash/boxes$", anyOf: ["page.cashLedger","page.cashSettings"] },
  { method: "GET", pattern: "^/api/finance/cash/transactions$", key: "page.cashLedger" },
  { method: "GET", pattern: "^/api/finance/cash/daily-summaries$", key: "page.cashLedger" },
  { method: "GET", pattern: "^/api/finance/cash/available-dates$", key: "page.cashLedger" },
  { method: "GET", pattern: "^/api/finance/cash/sessions$", key: "page.cashLedger" },
  { method: "POST", pattern: "^/api/finance/cash/transactions$", key: "action.cashLedger.recordEntry" },
  { method: "POST", pattern: "^/api/invoices/payment-vouchers$", key: "action.cashLedger.recordEntry" },
  { method: "POST", pattern: "^/api/finance/cash/close-day$", key: "action.cashLedger.closeDay" },
  { method: "GET", pattern: "^/api/finance/cash/refunds/sources$", key: "action.cashLedger.refundCustomer" },
  { method: "GET", pattern: "^/api/finance/cash/refunds/meta$", key: "action.cashLedger.refundCustomer" },
  { method: "POST", pattern: "^/api/finance/cash/refunds$", key: "action.cashLedger.refundCustomer" },
  { method: "POST", pattern: "^/api/finance/cash/refunds/[^/]+/retry-qbo$", key: "action.cashLedger.refundCustomer" },
  { method: "GET", pattern: "^/api/invoices/payment/queue$", key: "page.paymentQueue" },
  { method: "GET", pattern: "^/api/radius-servers$", anyOf: ["page.billing","action.billing.viewAllServers","page.serviceDiscounts","action.serviceDiscount.viewAllServers","page.qboSyncReview","action.qboSync.viewAllServers","page.receivePayment","action.payment.viewAllServers","page.hotspotPayments","action.hotspotPayment.viewAllServers","page.paymentsList","action.paymentsList.viewAllServers","page.paymentQueue","action.paymentQueue.viewAllServers","page.offlineCustomers","action.offlineCustomer.viewAllServers","page.settings.radiusServers","page.installations","page.hotspotParents","page.hotspotCustomers","page.hotspotCreatePackage","page.hotspotAllowances","action.hotspotParent.create","action.hotspotParent.edit","action.hotspotCustomer.create","action.hotspotCustomer.edit","action.hotspotPackage.create"] },
  { method: "POST", pattern: "^/api/invoices/payment/queue/[^/]+/retry$", key: "action.paymentQueue.retry" },
  { method: "PUT", pattern: "^/api/invoices/payment/queue/[^/]+$", key: "action.paymentQueue.editPayload" },
  { method: "GET", pattern: "^/api/invoices/payment/queue/[^/]+$", key: "action.paymentQueue.editPayload" },
  { method: "GET", pattern: "^/api/invoices/transactions$", key: "page.paymentsList" },
  { method: "GET", pattern: "^/api/invoices/payment-records$", key: "page.paymentsList" },
  { method: "GET", pattern: "^/api/invoices/payment-records/[^/]+$", key: "page.paymentsList" },
  { method: "GET", pattern: "^/api/radius-servers$", anyOf: ["page.billing","action.billing.viewAllServers","page.serviceDiscounts","action.serviceDiscount.viewAllServers","page.qboSyncReview","action.qboSync.viewAllServers","page.receivePayment","action.payment.viewAllServers","page.hotspotPayments","action.hotspotPayment.viewAllServers","page.paymentsList","action.paymentsList.viewAllServers","page.paymentQueue","action.paymentQueue.viewAllServers","page.offlineCustomers","action.offlineCustomer.viewAllServers","page.settings.radiusServers","page.installations","page.hotspotParents","page.hotspotCustomers","page.hotspotCreatePackage","page.hotspotAllowances","action.hotspotParent.create","action.hotspotParent.edit","action.hotspotCustomer.create","action.hotspotCustomer.edit","action.hotspotPackage.create"] },
  { method: "GET", pattern: "^/api/invoices/transactions/export$", key: "action.payment.export" },
  { method: "PUT", pattern: "^/api/invoices/payment-records/[^/]+$", key: "action.paymentRecord.edit" },
  { method: "DELETE", pattern: "^/api/invoices/payment-records/[^/]+$", key: "action.paymentRecord.delete" },
  { method: "GET", pattern: "^/api/invoices/reseller-customers/search$", key: "page.hotspotPayments" },
  { method: "GET", pattern: "^/api/radius-servers$", anyOf: ["page.billing","action.billing.viewAllServers","page.serviceDiscounts","action.serviceDiscount.viewAllServers","page.qboSyncReview","action.qboSync.viewAllServers","page.receivePayment","action.payment.viewAllServers","page.hotspotPayments","action.hotspotPayment.viewAllServers","page.paymentsList","action.paymentsList.viewAllServers","page.paymentQueue","action.paymentQueue.viewAllServers","page.offlineCustomers","action.offlineCustomer.viewAllServers","page.settings.radiusServers","page.installations","page.hotspotParents","page.hotspotCustomers","page.hotspotCreatePackage","page.hotspotAllowances","action.hotspotParent.create","action.hotspotParent.edit","action.hotspotCustomer.create","action.hotspotCustomer.edit","action.hotspotPackage.create"] },
  { method: "POST", pattern: "^/api/invoices/hotspot-payment/queue$", anyOf: ["page.hotspotPayments","action.payment.resellerRecharge"] },
  { method: "GET", pattern: "^/api/qbo-sync-unsynced/all-items$", key: "page.qboSyncReview" },
  { method: "GET", pattern: "^/api/qbo-sync-unsynced/items$", key: "page.qboSyncReview" },
  { method: "GET", pattern: "^/api/qbo-sync-unsynced/runs$", key: "page.qboSyncReview" },
  { method: "GET", pattern: "^/api/radius-servers$", anyOf: ["page.billing","action.billing.viewAllServers","page.serviceDiscounts","action.serviceDiscount.viewAllServers","page.qboSyncReview","action.qboSync.viewAllServers","page.receivePayment","action.payment.viewAllServers","page.hotspotPayments","action.hotspotPayment.viewAllServers","page.paymentsList","action.paymentsList.viewAllServers","page.paymentQueue","action.paymentQueue.viewAllServers","page.offlineCustomers","action.offlineCustomer.viewAllServers","page.settings.radiusServers","page.installations","page.hotspotParents","page.hotspotCustomers","page.hotspotCreatePackage","page.hotspotAllowances","action.hotspotParent.create","action.hotspotParent.edit","action.hotspotCustomer.create","action.hotspotCustomer.edit","action.hotspotPackage.create"] },
  { method: "POST", pattern: "^/api/qbo-sync-unsynced/admin/scan-all-sib-users$", key: "action.qboSync.pullUnsynced" },
  { method: "POST", pattern: "^/api/qbo-sync-unsynced/admin/scan-new-delta-users$", key: "action.qboSync.pullUnsynced" },
  { method: "PATCH", pattern: "^/api/qbo-sync-unsynced/items/[^/]+$", anyOf: ["action.offlineCustomer.link","action.qboSync.resolveConflicts"] },
  { method: "POST", pattern: "^/api/qbo-sync-unsynced/items/batch-save$", key: "action.qboSync.resolveConflicts" },
  { method: "POST", pattern: "^/api/qbo-customer-links/save-from-review$", key: "action.qboSync.resolveConflicts" },
  { method: "POST", pattern: "^/api/qbo-sync-unsynced/admin/items/[^/]+/recheck-match$", key: "action.qboSync.resolveConflicts" },
  { method: "POST", pattern: "^/api/qbo-sync-unsynced/admin/items/recheck-match-bulk$", key: "action.qboSync.resolveConflicts" },
  { method: "GET", pattern: "^/api/hotspot-customers$", anyOf: ["page.hotspotParents","page.hotspotCreatePackage","page.hotspotAllowances"] },
  { method: "GET", pattern: "^/api/hotspot-customers/sib-lookups$", key: "page.hotspotParents" },
  { method: "GET", pattern: "^/api/hotspot-customers/next-reseller-code$", key: "page.hotspotParents" },
  { method: "GET", pattern: "^/api/hotspot-customers/qbo-search$", key: "page.hotspotParents" },
  { method: "GET", pattern: "^/api/hotspot-customers/qbo-snapshot$", key: "page.hotspotParents" },
  { method: "GET", pattern: "^/api/hotspot-customers/[^/]+$", anyOf: ["page.hotspotParents","page.hotspotCreatePackage","page.hotspotAllowances"] },
  { method: "GET", pattern: "^/api/hotspot-customers/[^/]+/allowances$", anyOf: ["page.hotspotParents","page.hotspotAllowances"] },
  { method: "GET", pattern: "^/api/hotspot-parents$", key: "page.hotspotParents" },
  { method: "GET", pattern: "^/api/hotspot-parents/sib-lookups$", key: "page.hotspotParents" },
  { method: "GET", pattern: "^/api/hotspot-parents/[^/]+$", key: "page.hotspotParents" },
  { method: "GET", pattern: "^/api/radius-servers$", key: "page.hotspotParents" },
  { method: "POST", pattern: "^/api/hotspot-customers$", key: "action.hotspotParent.create" },
  { method: "POST", pattern: "^/api/hotspot-parents$", key: "action.hotspotParent.create" },
  { method: "PUT", pattern: "^/api/hotspot-customers/[^/]+$", key: "action.hotspotParent.edit" },
  { method: "PUT", pattern: "^/api/hotspot-parents/[^/]+$", key: "action.hotspotParent.edit" },
  { method: "DELETE", pattern: "^/api/hotspot-customers/[^/]+$", key: "action.hotspotParent.delete" },
  { method: "DELETE", pattern: "^/api/hotspot-parents/[^/]+$", key: "action.hotspotParent.delete" },
  { method: "GET", pattern: "^/api/hotspot-customers$", key: "page.hotspotCreatePackage" },
  { method: "GET", pattern: "^/api/hotspot-customers/[^/]+$", key: "page.hotspotCreatePackage" },
  { method: "GET", pattern: "^/api/hotspot-customers/[^/]+/package-options$", key: "page.hotspotCreatePackage" },
  { method: "GET", pattern: "^/api/hotspot-customers/[^/]+/packages$", key: "page.hotspotCreatePackage" },
  { method: "GET", pattern: "^/api/hotspot-customers/[^/]+/packages/[^/]+/users$", anyOf: ["page.hotspotCreatePackage","page.hotspotParents"] },
  { method: "GET", pattern: "^/api/radius-servers$", key: "page.hotspotCreatePackage" },
  { method: "POST", pattern: "^/api/hotspot-customers/[^/]+/packages$", key: "action.hotspotPackage.create" },
  { method: "GET", pattern: "^/api/hotspot-customers$", key: "page.hotspotAllowances" },
  { method: "GET", pattern: "^/api/hotspot-customers/[^/]+/allowances$", anyOf: ["page.hotspotParents","page.hotspotAllowances"] },
  { method: "GET", pattern: "^/api/hotspot-parents/[^/]+/allowances$", anyOf: ["page.hotspotParents","page.hotspotAllowances"] },
  { method: "POST", pattern: "^/api/hotspot-customers/[^/]+/allowances/[^/]+/report$", anyOf: ["page.hotspotParents","page.hotspotAllowances"] },
  { method: "POST", pattern: "^/api/hotspot-customers/[^/]+/allowances$", key: "action.hotspotAllowance.grant" },
  { method: "PATCH", pattern: "^/api/hotspot-customers/[^/]+/allowances/[^/]+$", key: "action.hotspotAllowance.grant" },
  { method: "POST", pattern: "^/api/hotspot-customers/[^/]+/allowances/[^/]+/close$", key: "action.hotspotAllowance.grant" },
  { method: "POST", pattern: "^/api/hotspot-parents/[^/]+/allowances$", key: "action.hotspotAllowance.grant" },
  { method: "GET", pattern: "^/api/branches", anyOf: ["page.branches","page.settings.radiusServers","page.users","page.installations","page.customerForm","page.offlineCustomers","page.resellers","page.serviceDiscounts","page.billing","page.paymentsList","page.paymentQueue","page.hotspotParents"] },
  { method: "POST", pattern: "^/api/branches$", key: "action.branch.create" },
  { method: "PUT", pattern: "^/api/branches/[^/]+$", key: "action.branch.edit" },
  { method: "DELETE", pattern: "^/api/branches/[^/]+$", key: "action.branch.delete" },
  { method: "GET", pattern: "^/api/users$", key: "page.users" },
  { method: "POST", pattern: "^/api/users/invite$", key: "action.user.invite" },
  { method: "PUT", pattern: "^/api/users/[^/]+$", key: "action.user.update" },
  { method: "POST", pattern: "^/api/users/re-invite/[^/]+$", key: "action.user.reInvite" },
  { method: "DELETE", pattern: "^/api/users/[^/]+$", key: "action.user.delete" },
  { method: "PUT", pattern: "^/api/users/[^/]+$", key: "action.user.managePermissions", when: (body) => body && typeof body === 'object' && 'permissions' in body },
  { method: "GET", pattern: "^/api/job-settings$", key: "page.settings.jobs" },
  { method: "GET", pattern: "^/api/job-settings/activate-pending-services$", key: "page.settings.jobs" },
  { method: "GET", pattern: "^/api/job-settings/hotspot-sib-packages-pull$", key: "page.settings.jobs" },
  { method: "GET", pattern: "^/api/job-settings/centers$", key: "page.settings.jobs" },
  { method: "PUT", pattern: "^/api/job-settings/activate-pending-services$", anyOf: ["page.settings.jobs","action.settings.jobs.edit"] },
  { method: "PUT", pattern: "^/api/job-settings/hotspot-sib-packages-pull$", anyOf: ["page.settings.jobs","action.settings.jobs.edit"] },
  { method: "POST", pattern: "^/api/job-settings/hotspot-sib-packages-pull/run$", anyOf: ["page.settings.jobs","action.settings.jobs.edit"] },
  { method: "GET", pattern: "^/api/quickbooks/token-status$", key: "action.qbo.readonly" },
  { method: "POST", pattern: "^/api/quickbooks/force-refresh$", key: "action.qbo.editable" },
  { method: "GET", pattern: "^/api/quickbooks/auth-url$", key: "action.qbo.editable" },
  { method: "GET", pattern: "^/api/google-drive/status$", key: "action.backup.readonly" },
  { method: "POST", pattern: "^/api/google-drive/backup-now$", key: "action.backup.editable" },
  { method: "GET", pattern: "^/api/google-drive/auth-url$", key: "action.backup.editable" },
  { method: "DELETE", pattern: "^/api/google-drive/revoke$", key: "action.backup.editable" },
  { method: "GET", pattern: "^/api/branches", key: "page.settings.radiusServers" },
  { method: "POST", pattern: "^/api/radius-servers$", key: "action.radiusServer.create" },
  { method: "PUT", pattern: "^/api/radius-servers/[^/]+$", key: "action.radiusServer.edit" },
  { method: "DELETE", pattern: "^/api/radius-servers/[^/]+$", key: "action.radiusServer.delete" },
  { method: "POST", pattern: "^/api/radius-servers/test$", key: "action.radiusServer.testConnection" },
  { method: "GET", pattern: "^/api/radius-servers/[^/]+/password$", key: "action.radiusServer.viewCredentials" }
]

export function pagePermissionForRoute(route: RouteLocationNormalized): string | null {
  const name = String(route.name || '')
  const path = route.path
  for (const page of routePages) {
    if (page.routeNames?.includes(name) || page.paths?.includes(path)) {
      return page.key
    }
  }
  return null
}

export function permissionForRequest(method: string, url: string, body?: any, query?: any): string | string[] | null {
  const cleanUrl = url.split('?')[0]
  let effectiveBody = body
  if (typeof body === 'string') {
    try {
      effectiveBody = JSON.parse(body)
    } catch {
      // not JSON string
    }
  }
  for (const matcher of matchers) {
    if (matcher.method === method && new RegExp(matcher.pattern).test(cleanUrl)) {
      if (!matcher.when || matcher.when(effectiveBody, query)) {
        return matcher.anyOf?.length ? matcher.anyOf : (matcher.key || null)
      }
    }
  }
  return null
}
